import { useEffect, useMemo, useState } from 'react';
import { useGetBonusCampaignQuery } from '../../../api/bonusCampaign';
import '../../../styles/_data.scss';
import '../../../index.css';
import Table from '../../../components/shared/Table';
import details_icon from '../../../images/details.svg';
import { actions } from '../../../redux/store/store';
import HeaderTextLayout from '../../../components/shared/HeaderTextLayout';
import { IoIosAddCircle } from 'react-icons/io';
import { CircularProgress } from '@mui/material';
import { handleBackgroundCheck } from '../../../constants/extras/statusesBadge';
import { useGetMetrosQuery } from '../../../api/metro';
import { CampaignStatus, CampaignType } from '../../../constants/menuLists';
import ReactSelect from 'react-select';
import ClearIcon from '@mui/icons-material/Clear';
import { IoSearchSharp } from 'react-icons/io5';
import MultiSelect from '../../../constants/multiSelectDropdown';
import { ReactComponent as DoneLogo } from '../../../images/logo-done.svg';
import { multiSelectStyle } from '../../../constants/multiSelectDropdown/multiSelectStyle';
import { Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { checkPermission } from '../../../auth/permissions';

export default function BonusCampaign() {
  const role = useSelector((state) => state.auth.currentUser?.role);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [metro, setMetro] = useState([]);

  const { data: metroList, isLoading: metroLoading } = useGetMetrosQuery();
  const {
    data: campaigns,
    isFetching,
    refetch
  } = useGetBonusCampaignQuery(
    {
      type: type?.value || '',
      status: status?.value || '',
      search,
      metros: JSON.stringify(metro?.map((m) => m?.value))
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (!metroLoading && metroList) {
      actions.campaign.setMetroList(
        metroList?.map((item) => ({
          id: item._id,
          value: item?.name,
          label: item?.name,
          labelByFirstName: item?.name
        }))
      );
    }
  }, [metroList, metroLoading]);

  const handleFilter = (clearSearch = false) => {
    setSearch(clearSearch ? '' : searchQuery);
  };

  const columns = useMemo(
    () => [
      {
        name: 'name',
        label: 'Name',
        sortable: true,
        maxWidth: '60px'
      },
      {
        name: 'type',
        label: 'Type',
        sortable: true,
        maxWidth: '40px'
      },
      {
        name: '',
        label: 'Campaign Date',
        sortable: true,
        maxWidth: '100px',
        renderer: ({ campaignDate }) =>
          `${campaignDate?.startDate?.split('T')?.[0]} - ${
            campaignDate?.endDate?.split('T')?.[0]
          }`
      },
      {
        name: 'amount',
        label: 'Amount',
        sortable: true,
        maxWidth: '30px',
        renderer: ({ amount }) => `$${amount.toFixed(2)}`
      },
      {
        name: 'minRoutes',
        label: 'Route Threshold',
        sortable: true,
        maxWidth: '30px'
      },
      {
        name: 'maxDays',
        label: 'Max Days',
        sortable: true,
        maxWidth: '30px'
      },
      {
        name: 'metros',
        label: 'Metros',
        sortable: true,
        maxWidth: '250px',
        renderer: ({ metros }) =>
          metros?.length === metroList?.length ? 'All' : metros?.join(', ')
      },
      {
        label: 'Admin',
        name: 'updated_by',
        sortable: true,
        maxWidth: '100px',
        renderer: ({ user_details }) => user_details?.username
      },
      {
        label: 'Status',
        name: 'isActive',
        sortable: true,
        maxWidth: '25px',
        className: 'table__td_centered table__td_min-horiz-padding',
        renderer: ({ isExpired, isActive }) => (
          <div style={{ padding: '5px 0' }}>
            <span
              className="textStyleForStatus"
              style={handleBackgroundCheck(isExpired ? false : isActive)}
            >
              {isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        )
      },
      {
        label: '',
        renderer: (item) =>
          !item?.isExpired && (
            <div
              onClick={() => {
                actions.modal.openCampaign(item);
              }}
              style={{ fontSize: '85%', cursor: 'pointer' }}
            >
              <img src={details_icon} alt="" width="20px" />
            </div>
          )
      }
    ],
    [metroList]
  );
  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div
            className="card"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <HeaderTextLayout
              headerText="Campaign List"
              className="col-lg-2 col-md-2 main__driver"
              data=""
              totalLength={
                campaigns?.length > 0 && campaigns?.length < 10
                  ? `${'0'.concat(campaigns?.length)} campaigns Found`
                  : `${campaigns?.length || 0} campaigns Found`
              }
            >
              <div className="col-lg-10 col-md-10 col-sm-12 filter">
                <div className="filter__inner p-0 justify-content-end">
                  <ReactSelect
                    options={CampaignType?.map((item) => ({
                      label: item,
                      value: item
                    }))}
                    isClearable={true}
                    onChange={(item) => {
                      setType(item);
                    }}
                    placeholder="Type"
                    value={type}
                    components={{
                      IndicatorSeparator: () => null
                    }}
                    styles={{
                      placeholder: (base) => ({
                        ...base,
                        fontSize: '13px',
                        fontWeight: 'normal'
                      }),
                      control: (styles) => ({
                        ...styles,
                        borderRadius: '5px',
                        boxShadow: 'none',
                        height: '2.8rem',
                        '&:hover': {
                          border: '2px solid #dadada'
                        },
                        border: '2px solid #dadada',
                        fontSize: '12px',
                        width: '140px'
                      })
                    }}
                  />
                  <ReactSelect
                    options={CampaignStatus?.map((item) => {
                      return {
                        value: item,
                        label: item
                      };
                    })}
                    isClearable={true}
                    onChange={(item) => {
                      setStatus(item);
                    }}
                    placeholder="Status"
                    value={status}
                    components={{
                      IndicatorSeparator: () => null
                    }}
                    styles={{
                      placeholder: (base) => ({
                        ...base,
                        fontSize: '13px',
                        fontWeight: 'normal'
                      }),
                      control: (styles) => ({
                        ...styles,
                        borderRadius: '5px',
                        boxShadow: 'none',
                        height: '2.8rem',
                        '&:hover': {
                          border: '2px solid #dadada'
                        },
                        border: '2px solid #dadada',
                        fontSize: '12px',
                        width: '140px'
                      })
                    }}
                  />
                  <div className="filter__ctrls-holder filter__input-holder d-flex justify-content-end search__container">
                    <div
                      className="searchbar__container"
                      style={{ width: '180px' }}
                    >
                      <input
                        className="form__input customized__input"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          if (e.target.value === '') {
                            setSearchQuery('');
                            handleFilter(true);
                          }
                        }}
                        placeholder="Quick Search"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleFilter();
                          }
                        }}
                        disabled={isFetching}
                      />
                      {searchQuery?.length > 0 && (
                        <ClearIcon
                          className="clear__btn filter__search-icon"
                          onClick={() => {
                            if (!isFetching && searchQuery?.length > 0) {
                              setSearchQuery('');
                              handleFilter(true);
                            }
                          }}
                          style={{
                            right: '45px',
                            top: '12px'
                          }}
                        />
                      )}
                      <button
                        disabled={searchQuery?.length <= 0}
                        className="search__button"
                        onClick={() => handleFilter()}
                      >
                        <IoSearchSharp />
                      </button>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end status_filter">
                    <MultiSelect
                      placeholder="Select Metros"
                      value={metro}
                      itemData={metroList?.map((item, i) => ({
                        value: item?.name ? item?.name : '-',
                        label: `${item?.name ? item?.name : ''}`
                      }))}
                      styles={{
                        ...multiSelectStyle(),
                        control: (styles) => ({
                          ...styles,
                          borderRadius: '5px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#dadada'
                          },
                          border: '2px solid #dadada !important',
                          borderRight: 'none !important',
                          borderTopRightRadius: '0 !important',
                          borderBottomRightRadius: '0 !important',
                          width: '180px',
                          height: '2.8rem',
                          overflowY: 'auto',
                          padding: 0
                        }),
                        menu: (styles) => ({
                          ...styles,
                          width: '250px'
                        })
                      }}
                      onChange={(value) => {
                        setMetro(value);
                      }}
                    />
                    <button
                      disabled={metro?.length < 1}
                      className="search__button"
                      onClick={() => {
                        handleFilter();
                      }}
                    >
                      <DoneLogo fill="#5fb6f7" width="24px" height="24px" />
                    </button>
                  </div>
                  {checkPermission(role, 'bonus.create') && <button
                    style={{
                      background: 'white',
                      borderColor: '#e6e6e6',
                      width: '180px'
                    }}
                    className="btn btn__accent mr-0"
                    onClick={() => actions.modal.openCampaign()}
                  >
                    <IoIosAddCircle style={{ fontSize: '22px' }} />
                    New Campaign
                  </button>}
                  <button className="refresh_btn" onClick={() => refetch()}>
                    <Refresh className="refresh_icon" />
                  </button>
                </div>
              </div>
            </HeaderTextLayout>

            {isFetching ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '80vh' }}
              >
                <CircularProgress className="loading-progress" />
              </div>
            ) : campaigns?.length ? (
              <div className="table-wrapper normal_table">
                <Table
                  columns={columns}
                  items={campaigns}
                  className="table_metros"
                />
              </div>
            ) : (
              <div className="no_results">No Results</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
